import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Typography } from '@mui/material';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import KeyIcon from '@mui/icons-material/Key';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CRUDSettings from '../components/General/CRUDSettings';
import {
    useApiKeys,
    useApiPermissions,
    useCreateApiKey,
    useUpdateApiKey,
    useDeleteApiKey,
    useGenerateApiToken,
} from '../hooks/api/apiKeys';
import ApiKeyDialog from '../components/ApiKeys/ApiKeyDialog';
import { ApiKeyForm, ApiKeyInterface, ApiTokenInterface } from '../types/ApiKey.interface';
import ApiTokenDialog from '../components/ApiKeys/ApiTokenDialog';
import { copyToClipboard } from '../utils';
import { useDispatchSuccessNotification } from '../hooks';
import ConfirmationDialog from '../components/UI/Dialogs/ConfirmationDialog';

const ApiKeys = () => {
    const { t } = useTranslation();
    const { dispatchSuccessNotification } = useDispatchSuccessNotification();

    const { data: apiKeys, isLoading, isFetching, isFetchedAfterMount } = useApiKeys();

    const { data: apiPermissions } = useApiPermissions();

    const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyInterface | undefined>(undefined);
    const [generatedApiToken, setGeneratedApiToken] = useState<ApiTokenInterface | undefined>(undefined);

    const [showApiKeyDialog, setShowApiKeyDialog] = useState<boolean>(false);
    const [showApiTokenDialog, setShowApiTokenDialog] = useState<boolean>(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);

    const isApiKeysLoading = useMemo(() => {
        return isFetchedAfterMount ? isLoading : isFetching;
    }, [isFetchedAfterMount, isLoading, isFetching]);

    const handleNewApiToken = useCallback(
        (token: ApiTokenInterface) => {
            setGeneratedApiToken(token);
            setShowApiTokenDialog(true);
        },
        [setGeneratedApiToken, setShowApiTokenDialog]
    );

    const { mutate: handleCreateApiKey, isLoading: isApiKeyCreating } = useCreateApiKey({
        onSettled: (data, error) => {
            if (!error) setShowApiKeyDialog(false);
            if (data?.token) handleNewApiToken(data.token);
        },
    });
    const { mutate: handleUpdateApiKey, isLoading: isApiKeyUpdating } = useUpdateApiKey({
        onSettled: (data, error) => {
            if (!error) setShowApiKeyDialog(false);
            if (data?.token) handleNewApiToken(data.token);
        },
    });
    const { mutate: handleDeleteApiKey } = useDeleteApiKey({
        onSettled: (data, error) => {
            if (!error) {
                setSelectedApiKey(undefined);
                setShowConfirmationDialog(false);
            }
        },
    });
    const { mutate: handleGenerateApiToken, isLoading: isTokenGenerating } = useGenerateApiToken({
        onSettled: (data, error) => {
            if (!error) setShowApiKeyDialog(false);
            if (data) handleNewApiToken(data);
        },
    });

    const handleFormSubmit = useCallback(
        (data: ApiKeyForm) => {
            if (data.id) {
                handleUpdateApiKey(data);
                return;
            }

            handleCreateApiKey(data);
        },
        [handleCreateApiKey, handleUpdateApiKey]
    );

    const handleGenerateClick = useCallback(
        ({ data }: ICellRendererParams) => {
            handleGenerateApiToken({
                client_id: data.client_id,
                client_secret: data.client_secret,
            });
        },
        [handleGenerateApiToken]
    );

    const onDeleteApiKey = useCallback(async (data: ApiKeyInterface) => {
        setSelectedApiKey(data);
        setShowConfirmationDialog(true);
    }, []);

    const confirmApiKeyDeletion = useCallback(
        (apiKey: ApiKeyInterface) => {
            handleDeleteApiKey(apiKey.client_id);
            setShowConfirmationDialog(false);
        },
        [handleDeleteApiKey]
    );

    const openCreateDialog = useCallback(
        (value: any) => {
            setShowApiKeyDialog(value);
        },
        [setShowApiKeyDialog]
    );

    const handleCopyValue = useCallback(
        async (value: string, name = 'Value') => {
            await copyToClipboard(value);
            dispatchSuccessNotification({ message: t('apiKey.copiedToClipboard', { name }) });
        },
        [dispatchSuccessNotification, t]
    );

    const copyCellValueRenderer = useCallback(
        ({ value, colDef }: ICellRendererParams) => {
            return (
                <>
                    <IconButton
                        aria-label={t('action.copyToClipboard')}
                        size="small"
                        onClick={() => handleCopyValue(value, colDef?.headerName)}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption">{value}</Typography>
                </>
            );
        },
        [handleCopyValue, t]
    );

    const columnDefs = useMemo<Array<ColDef>>(
        () => [
            {
                field: 'name',
                headerName: t('field.name'),
            },
            {
                field: 'scope',
                headerName: t('field.scopes'),
                valueGetter: ({ data }) => t('apiKey.scopesCount', { count: (data.scope || []).length }),
            },
            {
                field: 'client_id',
                headerName: t('field.clientId'),
                cellRenderer: copyCellValueRenderer,
            },
            {
                field: 'client_secret',
                headerName: t('field.clientSecret'),
                cellRenderer: copyCellValueRenderer,
            },
            {
                field: '',
                headerName: t('field.accessToken'),
                editable: false,
                filter: false,
                suppressHeaderMenuButton: true,
                cellRenderer: (params: ICellRendererParams) => {
                    return (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleGenerateClick(params)}
                            startIcon={<KeyIcon />}
                            sx={{ py: 0 }}
                            disabled={isTokenGenerating}
                        >
                            {t('apiKey.generate')}
                        </Button>
                    );
                },
            },
        ],
        [copyCellValueRenderer, handleGenerateClick, isTokenGenerating, t]
    );

    return (
        <>
            <CRUDSettings
                tableRows={apiKeys}
                columnDefs={columnDefs}
                btnText={t('apiKey.addNew')}
                recordType="API Key"
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
                isCreationViaDialog={true}
                isEditViaDialog={true}
                setShowCreationDialog={openCreateDialog}
                onSelectedRowChange={setSelectedApiKey}
                loading={isApiKeysLoading}
                hideIdColumn={true}
                rowHeight={48}
                showDeleteButton={true}
                onDelete={onDeleteApiKey}
            />
            {showApiKeyDialog && (
                <ApiKeyDialog
                    apiKey={selectedApiKey}
                    showDialog={showApiKeyDialog}
                    permissions={apiPermissions}
                    onSubmit={handleFormSubmit}
                    onClose={() => setShowApiKeyDialog(false)}
                    isLoading={isApiKeyCreating || isApiKeyUpdating}
                />
            )}
            {showApiTokenDialog && generatedApiToken && (
                <ApiTokenDialog
                    apiToken={generatedApiToken}
                    showDialog={showApiTokenDialog}
                    onClose={() => setShowApiTokenDialog(false)}
                    closeOnBackdropClick={false}
                />
            )}
            {showConfirmationDialog && selectedApiKey && (
                <ConfirmationDialog
                    open={showConfirmationDialog}
                    title={t('apiKey.deleteTitle')}
                    text={t('apiKey.deleteConfirm', { name: selectedApiKey.name })}
                    onClose={() => setShowConfirmationDialog(false)}
                    onConfirm={() => confirmApiKeyDeletion(selectedApiKey)}
                />
            )}
        </>
    );
};

export default ApiKeys;
